import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetChallanList,
  GetChallanDetail,
  CreateChallan,
  UpdateChallan,
  DeleteChallan,
} from "../../Services/ChallanService";

/**
 * Challan State Interface with caching and optimization
 */
export interface ChallanState {
  // List data
  challanCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  
  // Detail data
  selectedChallanDetail: any | null;
  
  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  
  // Error states
  error: string | null;
  errorDetail: string | null;
  
  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  
  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;
  
  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `challan_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchChallanList = createAsyncThunk(
  "challan/fetchChallanList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { challan: ChallanState };
      const cacheKey = generateCacheKey(params);
      
      if (root.challan.cache.has(cacheKey)) {
        const cached = root.challan.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetChallanList(urlParams.toString());
      
      if (result?.data?.data?.challanCollection) {
        return result.data.data;
      }
      
      return rejectWithValue("No challan data received");
    } catch (error: any) {
      console.error("Error fetching challan list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch challans"
      );
    }
  }
);

export const fetchChallanDetail = createAsyncThunk(
  "challan/fetchChallanDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      challanID: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("challanID", params.challanID.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetChallanDetail(urlParams.toString());
      
      if (result?.data?.data) {
        return result.data.data;
      }
      
      return rejectWithValue("No challan detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch challan details"
      );
    }
  }
);

export const createChallanAsync = createAsyncThunk(
  "challan/createChallan",
  async (challanData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateChallan(challanData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to create challan");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create challan"
      );
    }
  }
);

export const updateChallanAsync = createAsyncThunk(
  "challan/updateChallan",
  async (challanData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateChallan(challanData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to update challan");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update challan"
      );
    }
  }
);

export const deleteChallanAsync = createAsyncThunk(
  "challan/deleteChallan",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteChallan(deleteData);
      if (result?.data) return deleteData;
      return rejectWithValue("Failed to delete challan");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete challan"
      );
    }
  }
);

const initialState: ChallanState = {
  challanCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedChallanDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const challanSlice = createSlice({
  name: "challan",
  initialState,
  reducers: {
    clearChallanData: (state) => {
      state.challanCollection = [];
      state.totalCount = 0;
      state.selectedChallanDetail = null;
      state.error = null;
    },
    clearChallanDetail: (state) => {
      state.selectedChallanDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChallanList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchChallanList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.challanCollection = action.payload?.challanCollection || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.pageIndex = action.payload?.pageIndex || 1;
        state.pageSize = action.payload?.pageSize || 10;
        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchChallanList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchChallanDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchChallanDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingDetail = false;
        state.selectedChallanDetail = action.payload;
      })
      .addCase(fetchChallanDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(createChallanAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createChallanAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createChallanAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateChallanAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateChallanAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateChallanAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteChallanAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteChallanAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteChallanAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearChallanData,
  clearChallanDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = challanSlice.actions;

export default challanSlice.reducer;
